import { pluginFactory } from '@atlaskit/editor-common/utils';
import type {
	EditorState,
	ReadonlyTransaction,
	SafeStateField,
	Transaction,
} from '@atlaskit/editor-prosemirror/state';
import { DecorationSet } from '@atlaskit/editor-prosemirror/view';

import { inlineSuggestionPluginKey } from './inline-suggestion-plugin-key';
import { reducer } from './reducer';
// eslint-disable-next-line import/order
import type { InlineSuggestionAction, InlineSuggestionState } from './states';
// eslint-disable-next-line import/order
import type { Dispatch } from '@atlaskit/editor-common/event-dispatcher';
// eslint-disable-next-line import/order
import type { Command } from '@atlaskit/editor-common/types';

function isSelectionNotInSuggestion({
	selectionFrom,
	selectionTo,
	suggestionFrom,
	suggestionTo,
}: {
	selectionFrom: number;
	selectionTo: number;
	suggestionFrom: number;
	suggestionTo: number;
}): boolean {
	return selectionTo < suggestionFrom || selectionFrom > suggestionTo;
}

function onSelectionChanged(
	tr: ReadonlyTransaction,
	pluginState: InlineSuggestionState,
): InlineSuggestionState {
	const { inlineSuggestion } = pluginState;
	if (!inlineSuggestion) {
		return pluginState;
	}

	const { from, to } = inlineSuggestion;
	const { from: selectionFrom, to: selectionTo } = tr.selection;

	if (
		isSelectionNotInSuggestion({
			selectionFrom: selectionFrom,
			selectionTo: selectionTo,
			suggestionFrom: from,
			suggestionTo: to,
		})
	) {
		return {
			...pluginState,
			inlineSuggestion: null,
			decorationSet: DecorationSet.empty,
			inlineSuggestionSessionId: null,
		};
	}

	return pluginState;
}

function mapSuggestionPositions(
	tr: ReadonlyTransaction,
	pluginState: InlineSuggestionState,
	_editorState: EditorState,
): InlineSuggestionState {
	const { inlineSuggestion } = pluginState;
	if (!inlineSuggestion) {
		return pluginState;
	}
	return {
		...pluginState,
		inlineSuggestion: {
			...inlineSuggestion,
			from: tr.mapping.map(inlineSuggestion.from),
			to: tr.mapping.map(inlineSuggestion.to),
		},
		decorationSet: pluginState.decorationSet.map(tr.mapping, tr.doc),
	};
}

export const createInitialState = ({ locale }: { locale: string }): InlineSuggestionState => {
	return {
		inlineSuggestion: null,
		locale,
		decorationSet: DecorationSet.empty,
		inlineSuggestionSessionId: null,
	};
};

const dest = pluginFactory(inlineSuggestionPluginKey, reducer, {
	onSelectionChanged,
	mapping: mapSuggestionPositions,
});
export const createPluginState: (
	dispatch: Dispatch,
	initialState: InlineSuggestionState | ((state: EditorState) => InlineSuggestionState),
) => SafeStateField<InlineSuggestionState> = dest.createPluginState;
export const createCommand: <A = InlineSuggestionAction>(
	action: A | ((state: Readonly<EditorState>) => false | A),
	transform?: (tr: Transaction, state: EditorState) => Transaction,
) => Command = dest.createCommand;
export const getPluginState: (state: EditorState) => InlineSuggestionState = dest.getPluginState;
